import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import { api } from "@/lib/api";

const fallbackTestimonials = [
  {
    name: "Sagar Savsani",
    trip: "Central Europe · Honeymoon",
    quote:
      "Ten days across Paris and Switzerland, and every detail — hotels in the heart of town, daily briefings, local history — was handled before we thought to ask. Easily the best money we've spent.",
  },
  {
    name: "Chinmay Shah",
    trip: "South Africa · Group Tour",
    quote:
      "First time in South Africa and it ran like clockwork. Transfers on time, beautiful hotels, and a tour manager who stayed on his toes the entire journey. Money well spent.",
  },
  {
    name: "Dishita Varmora",
    trip: "Thailand · Friends Getaway",
    quote:
      "Phuket, Krabi, Bangkok — the resort picks were stunning and the team was virtually with us the whole trip. We called at odd hours and still got warm, patient answers.",
  },
  {
    name: "Jainil Sanghvi",
    trip: "Bali · Customised Tour",
    quote:
      "They built the package around us — river rafting, Nusa Penida, even Jain meals sorted without a fuss. A lifetime experience, end to end.",
  },
  {
    name: "Dr. Alpa Chavda",
    trip: "Canada · Group Tour",
    quote:
      "The Canadian Rockies are heaven, and the service was the topping — our third international tour with them, and the caring tour managers made it the best yet.",
  },
];

export const TestimonialsCarousel = () => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [testimonials, setTestimonials] = useState(fallbackTestimonials);

  useEffect(() => {
    api
      .get("/testimonials")
      .then((res) => {
        if (res.data?.length) setTestimonials(res.data);
      })
      .catch(() => {});
  }, []);

  const go = (dir) => {
    setDirection(dir);
    setIndex((prev) => (prev + dir + testimonials.length) % testimonials.length);
  };

  const t = testimonials[index % testimonials.length];
  const rating = Math.min(5, Math.max(1, Number(t.rating) || 5));

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-20 md:py-28 px-4 sm:px-8 lg:px-16 bg-surface/40">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-gold mb-4">03 — Happy Travellers</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-cream">
            Stories from the <span className="italic text-gold">road.</span>
          </h2>
        </motion.div>

        <div className="relative rounded-3xl border border-gold/15 bg-card/60 px-6 sm:px-14 py-12 sm:py-16 overflow-hidden">
          <Quote size={120} className="absolute -top-6 -left-6 text-gold/10" strokeWidth={1} />
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={index}
              custom={direction}
              initial={{ opacity: 0, x: direction * 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction * -60 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10"
              data-testid="testimonial-card"
            >
              <div className="flex gap-1 mb-6 justify-center">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className={i < rating ? "text-gold fill-gold" : "text-cream/20"} />
                ))}
              </div>
              <p className="font-display text-xl sm:text-2xl leading-relaxed text-cream text-center italic">
                "{t.quote}"
              </p>
              <div className="mt-8 flex items-center justify-center gap-4">
                <span className="w-12 h-12 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center font-display text-lg text-gold">
                  {t.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                </span>
                <div className="text-left">
                  <p className="text-sm font-semibold text-cream" data-testid="testimonial-author">{t.name}</p>
                  <p className="text-xs font-mono tracking-widest uppercase text-faded">{t.trip}</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-8 flex items-center justify-center gap-6">
          <button
            onClick={() => go(-1)}
            data-testid="testimonial-prev-btn"
            className="w-11 h-11 rounded-full border border-cream/20 flex items-center justify-center text-mist hover:border-gold hover:text-gold transition-colors"
            aria-label="Previous testimonial"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex gap-2">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => { setDirection(i > index ? 1 : -1); setIndex(i); }}
                data-testid={`testimonial-dot-${i}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-8 bg-gold" : "w-2 bg-cream/20"}`}
                aria-label={`Go to testimonial ${i + 1}`}
              />
            ))}
          </div>
          <button
            onClick={() => go(1)}
            data-testid="testimonial-next-btn"
            className="w-11 h-11 rounded-full border border-cream/20 flex items-center justify-center text-mist hover:border-gold hover:text-gold transition-colors"
            aria-label="Next testimonial"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};
